import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { permissionsObj } from '../../../../../../OnBoarding/Organization/RolesAndPermissions/constants/roles-permissions.constants';
import { BuilderServices } from '../../../../../../../services/builder';
import {
  FormFieldTypes,
  LoadingStatus,
  toastMessage,
  ToastThemes,
} from '../../../../../../../constants/common';
import { GeoTagging } from '../../../../../../../services/geoTagging';
import { showToast } from '../../../../../../../utils/common.util';
import { getFormBody } from '../../../../../../OnBoarding/Organization/utils/userOnboarding/userOnboarding.util';
import {
  stringSubstitute,
  ToastMessages,
} from '../../../../../../OnBoarding/Organization/constants/onboarding.constants';
import { Box } from '@mui/material';
import CircularLoader from '../../../../../../../atoms/CircularLoader/circular-loader';
import FormEdit from '../../../../../../OnBoarding/common/Form/FormEdit';
import { getFinancialYear } from '../../../../../../../utils/formatter';

const TDSEditForm = (props) => {
  const {
    formConfig,
    claimTypeData,
    claimFormData = {},
    setViewMode,
    claimTypeId,
    claimId,
    allowEdit,
    enitilements,
    resubmittedClaim,
    validationErrors,
    setValidationErrors,
    setFormInfo,
  } = props;

  const { referenceNumber, createdBy, ...rest } = claimFormData || {};
  const [formState, setFormState] = useState(rest || {});
  const [dropDownOptions, setDropDownOptions] = useState({});
  const [loading, setLoading] = useState(-1);
  const [alreadyApproved, setAlreadyApproved] = useState(false);
  const formStateRef = useRef(formState);

  const { currentUserRoleId, userInfo, isMobile } = useSelector(
    (state) => state.main
  );
  const { publicHoliday, logoImage } = useSelector((state) => state && state.onBoarding);
    const payrollFY = logoImage?.payrollFY; 
  const { compBenPermission } = useSelector((state) => state.modulePermissions);
  const fy = getFinancialYear(payrollFY);

  const moduleName = Object.keys(compBenPermission).find(
    (key) => compBenPermission[key].id === Number(claimTypeId)
  );
  const permissions =
    compBenPermission?.[moduleName]?.[permissionsObj.allowAdd];

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    setFormState(rest);
  }, [claimFormData]);

  useEffect(() => {
    formStateRef.current = formState;
  }, [formState]);

  const handleBasedSSL = async (event) => {
    const key = event.target.name;
    const val = event.target.value;

    if (val) {
      try {
        const financialYear = formStateRef.current.financialYear;

        if (!financialYear) {
          showToast('Please select financial year first.', ToastThemes.error);
          return;
        }
        setLoading(LoadingStatus.Loading);
        const response = await new GeoTagging().getTDSDetails(
          claimTypeId,
          '',
          currentUserRoleId,
          financialYear,
          val?.id
        );
        setAlreadyApproved(response?.auditTrail?.length > 0);
        const {
          financialYear: year,
          salaryComponent = {},
          createdBy,
          ...other
        } = response?.info || {};
        const res = {
          id: null,
          proposedAmount: null,
          receiptAmount: null,
          receiptDate: null,
          receiptDocument: null,
        };
        setLoading(LoadingStatus.Success);
        if (response?.info) {
          setFormState((prev) => ({
            ...prev,
            [key]: val,
            ...other,
          }));
        } else {
          setFormState((prev) => ({
            ...prev,
            [key]: val,
            ...res,
          }));
        }

        setValidationErrors((prev) => ({ ...prev, [key]: '' }));
      } catch (err) {
        setLoading(LoadingStatus.Failure);
        showToast(err?.response?.data?.message, ToastThemes.error);
        throw err?.response?.data?.message;
      }
    }
  };

  const formFields = useMemo(() => {
    return formConfig?.reduce((acc, item) => {
      const { fieldRefName, sysField } = item;
      let temp = { ...item };
      switch (fieldRefName) {
        case 'salaryComponent': {
          temp = {
            ...temp,
            onChange: handleBasedSSL,
          };
          break;
        }
        case 'financialYear': {
          temp = {
            ...temp,
            minDate: fy.start,
            maxDate: fy.start,
          };
          break;
        }
        default: {
          break;
        }
      }
      acc.push(temp);
      return acc;
    }, []);
  }, [formConfig]);

  const getDropdownOptions = (name, id) => {
    switch (name) {
      default: {
        return dropDownOptions[name];
      }
    }
  };

  const handleDropdownClick = async (name, id) => {
    switch (name) {
      default: {
        return new BuilderServices().getDropDownOption(id).then((res) => {
          setDropDownOptions({ ...dropDownOptions, [name]: res });
          return true;
        });
      }
    }
  };

  const removeAutoPopulatedFields = () => {
    const updatedFormdata = { ...formState };
    formFields.forEach((field) => {
      if (
        (field.fieldType === FormFieldTypes.AutoPopulate ||
          field.fieldType === FormFieldTypes.ProRateField) &&
        field.fieldRefName in updatedFormdata
      ) {
        delete updatedFormdata[field.fieldRefName];
      }
    });
    return updatedFormdata;
  };

  const handleSaveClick = async () => {
    if (alreadyApproved) {
      showToast(
        'Actual amount already submitted. Please check the status of your exemption.',
        ToastThemes.error
      );
      setTimeout(() => {
        navigate(-1);
      }, 1000);
      return;
    }
    const res = await postClaim();
    setViewMode(true);
    if (isMobile) {
      navigate('/home');
    } else {
      setLoading(LoadingStatus.Loading);
      new GeoTagging()
        .getTDSDetails(claimTypeId, res.id, currentUserRoleId)
        .then((res) => {
          setFormInfo(res);
          setLoading(LoadingStatus.Success);
        })
        .catch((err) => {
          setLoading(LoadingStatus.Failure);
          showToast(err?.response?.data?.message, ToastThemes.error);
          throw err?.response?.data?.message;
        });
      navigate(`/comp-ben/${claimTypeId}/${res.id}`, {
        replace: true,
      });
    }
  };

  const postClaim = async () => {
    try {
      const newFormData = removeAutoPopulatedFields();
      const formConfig = getFormBody({
        ...newFormData,
        status: formState?.receiptAmount ? 'PENDING' : 'SUBMITTED',
      });
      const { createdAt, actualBalance, ...claimConfigRest } = formConfig;
      setLoading(LoadingStatus.Loading);
      const payloadData = {
        formConfig: claimConfigRest,
        data: { id: claimTypeId, isAWFAttached: claimTypeData.isAWFAttached },
        currentUserRoleId,
        allowEdit,
        resubmittedClaim,
      };

      const res = await new GeoTagging().postTDS(payloadData);
      setLoading(LoadingStatus.Success);
      showToast(
        ToastMessages.success.replace(stringSubstitute, toastMessage.success),
        ToastThemes.success
      );
      return res;
    } catch (err) {
      setLoading(LoadingStatus.Failure);
      showToast(err?.response?.data?.message, ToastThemes.error);
      throw err?.response?.data?.message;
    }
  };

  const handleDiscard = () => {
    if (isNaN(claimId)) {
      navigate(-1);
    } else {
      setViewMode(true);
    }
  };

  // const handleSaveAndAddNewClick = async () => {
  //   await postClaim();
  //   setFormState({});
  // };

  // const handleSaveAndAddCopyClick = async () => {
  //   await postClaim();
  //   const {
  //     id,
  //     referenceNumber,
  //     status,
  //     createdUtc,
  //     fkCreatedBy,
  //     actualBalance,
  //     ...rest
  //   } = formState;
  //   setFormState(rest);
  // };

  if (!permissions) {
    return 'ACCESS DENIED';
  }
  return (
    <Box>
      <CircularLoader show={LoadingStatus.Loading === loading} />
      <FormEdit
        list={formFields}
        formInfo={formState}
        setFormData={setFormState}
        handleDropdownClick={handleDropdownClick}
        getDropdownOptions={getDropdownOptions}
        errors={validationErrors}
        setErrors={setValidationErrors}
        isActionHeader={true}
        publicHoliday={publicHoliday}
        actionHeader={{
          labelText: claimTypeData?.name || 'New Form',
          showDiscard: true,
          showPublish: true,
          showPublishModal: false,
          publishButtonText: 'Submit',
          onPublishClick: handleSaveClick,
          showSave: false,
          onClickDiscard: handleDiscard,
          labelCode: referenceNumber || '',
          // handleSaveAndAddNewClick: handleSaveAndAddNewClick,
          // handleSaveAndAddCopyClick: handleSaveAndAddCopyClick,
          // firstButton: (onClick) =>
          //   !isMobile &&
          //   isNaN(claimId) && (
          //     <StyledButton onClick={onClick}>
          //       <ContentCopyOutlined
          //         sx={{ width: '24px', height: '24px', mr: '5px' }}
          //       />
          //       Submit & Copy
          //     </StyledButton>
          //   ),
          // secondButton: (onClick) =>
          //   !isMobile &&
          //   isNaN(claimId) &&
          //   claimTypeData?.claimTypeCode !== 'SC' && (
          //     <StyledButton onClick={onClick}>
          //       <AddOutlined
          //         sx={{ width: '24px', height: '24px', mr: '5px' }}
          //       />
          //       Submit & New
          //     </StyledButton>
          //   ),
        }}
      />
    </Box>
  );
};

export default TDSEditForm;
